<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-date-picker
                        range
                        :max-date="maxDate"
                        :range-inline="false"
                        start-label="Período de"
                        end-label="Até"
                        @change="resetFields"
                        v-model="model.periodo"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :disabled="!data.additionals.isSupervisor"
                        :options="unidades"
                        label="Unidade executante"
                        show-default-option
                        text-field="unidadeNome"
                        @change="resetMedico"
                        v-model="model.unidadeId"
                        value-field="unidadeId"
                        :validations="validations.unidadeId"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :options="profissionais"
                        label="Profissional executante"
                        show-default-option
                        text-field="profissionalNome"
                        v-model="model.profissionalId"
                        :validations="validations.profissionalId"
                        value-field="profissionalId"
                        @change="reset"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-select
                        :options="medicos"
                        label="Médico solicitante"
                        show-default-option
                        text-field="medicoNome"
                        default-option-text="Todos"
                        @change="resetProfissional"
                        v-model="model.medicoId"
                        value-field="medicoId"
                        :validations="validations.medicoId"
                    />
                </rkm-col>

                <rkm-col cols="4">
                     <rkm-form-select
                        :options="grupoExames"
                        label="Grupo de exames"
                        show-default-option
                        text-field="grupoExame"
                        v-model="model.grupoExameId"
                        value-field="grupoExameId"
                        :validations="validations.grupoExameId"
                    />
                </rkm-col>

                <rkm-col cols="4">

                    <rkm-form-select
                        :options="exames"
                        label="Exames"
                        show-default-option
                        text-field="exameNome"
                        default-option-text="Todos"
                        v-model="model.exameId"
                        value-field="exameId"
                        :validations="validations.exameId"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :repository="repositories.paciente"
                        :search-fields="fields.paciente"
                        :text-field="(item) => item.cidadao.nome"
                        label="Paciente"
                        v-model="paciente"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        label="Agendamento"
                        v-model="model.agendamentoNumero"
                        number
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        label="Prontuário"
                        v-model="model.prontuario"
                        number
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        @click="$refs.Form.validate(search)"
                        color="primary"
                        class="float-left"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click.stop="resetForm"
                        color="secondary"
                        class="mr-2 float-left"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-row
            v-if="data.model.agendamentosNotSave"
            class="mt-2 mb-2"
        >
            <rkm-col>
                <template>
                    <rkm-button
                        @click="selectRealizado()"
                        color="info"
                        class="mb-2"
                    >
                        Atribuir todos como realizado
                    </rkm-button>
                </template>

                <template>
                    <rkm-button
                        @click="selectFaltou()"
                        color="danger"
                        class="mb-2"
                    >
                        Atribuir todos como faltou
                    </rkm-button>
                </template>

                <template>
                    <rkm-button
                        @click="cleanSelecionados()"
                        color="secondary"
                        class="mb-2"
                    >
                        Limpar
                    </rkm-button>
                </template>
            </rkm-col>
        </rkm-row>

        <rkm-table
            v-if="data.model.agendamentosNotSave"
            :fields="fields.tables.agendamento"
            :items="data.model.agendamentosNotSave || data.model.agendamentos"
            ref="Table"
            sort-by="data"
            hide-filters
        >
            <template v-slot:cell(checkbox)="{ item }">
                <rkm-form-checkbox
                    v-model="item.selecionado"
                    @change="selectVarios"
                />
            </template>

            <template v-slot:cell(pacienteIdade)="row">
                {{ row.item.pacienteIdade }}<span v-if="row.item.pacienteIdade > 2"> anos</span>
            </template>

            <template v-slot:cell(pacienteNome)="row">
                <rkm-button
                    class="mr-2"
                    color="secondary"
                    small
                    v-rkm-popover.hover.right="
                        {
                            title: 'Dados cadastrais',
                            html: true,
                            content:
                                '<b>Prontuário:</b> ' + (row.item.prontuario || '') + '<br/>' +
                                '<b>Data de Nascimento:</b> ' + row.item.dataNascimento + '<br/>' +
                                '<b>Nome da Mãe:</b> ' + row.item.nomeMae + '<br/>'
                        }
                    "
                >
                    <rkm-icon icon="user" />
                </rkm-button>

                <span v-if="!row.item.sequencialDesconhecido">
                    {{row.item.pacienteNome}}

                    <template v-if="row.item.nomeSocial">
                        <template v-if="row.item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>({{ row.item.nomeSocial }})</strong>
                        </template>

                        <template v-else>
                            <strong>({{ row.item.nomeSocial }})</strong>
                        </template>
                    </template>
                </span>

                <span
                    v-else
                    class="text-primary"
                    v-rkm-popover.hover.top="
                        {
                            title: 'Características físicas',
                            content: row.item.caracteristicasFisicas
                        }
                    "
                >
                    {{row.item.pacienteNome}}
                </span>
            </template>

            <template v-slot:cell(status)="{ item }">

                <rkm-form-select
                    :options="filteredStatusOptions(item.regulacaoSolicitacaoId)"
                    :label="false"
                    text-field="nome"
                    value-field="id"
                    v-model="item.statusId"
                    show-default-option
                    class="pt-0 mb-0"
                    @change="item.protocoloFechamento = null"              
                />  
            </template>

            <template v-slot:cell(numero)="{item}">
                <rkm-form-input
                    :disabled="!item.controlaProtocoloFechamento || !item.numero || item.statusId !== enums.statusFechamento.REALIZADO"
                    :label="false"
                    @change="handleProtocoloFechamentoChange(item)"
                    class="pt-0 mb-0"
                    v-model="item.protocoloFechamento"
                />
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    :id="uniqueId('show-btn')"
                    @click="$refs.Modal.open(item)"
                    title="Detalhamento"
                    small
                    color="primary"
                >
                    <rkm-icon icon="search" />
                </rkm-button>
            </template>
        </rkm-table>

        <Modal
            ref="Modal"
            :data="data"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { repository, error, object, data, stringCase } from '@/mixins'

// Components.
import Modal from '@/components/exame-imagem/fechamento/components/Modal'

// Moment.
import moment from 'moment'

// Enuns.
import statusFechamento from '@/enums/exame-imagem/fechamento/status'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'

export default {
    components:{ Modal },
    computed: {
        ...mapGetters({centroCusto: 'getCentroCusto'}),
        fields() {

            return {
                tables: {
                    agendamento: [
                        { key: 'data', label: 'Data do agendamento', class: 'col-max', sortable: true },
                        { key: 'pacienteNome', label: 'Paciente', class: 'col-max', sortable: true },
                        { key: 'pacienteIdade', label: 'Idade', class: 'col-max', sortable: true },
                        { key: 'prontuario', label: 'Prontuário', class: 'col-max', sortable: true },
                        { key: 'agendamentoNumero', label: 'Agendamento', class: 'col-max', sortable: true },
                        { key: 'exameNome', label: 'Exame', class: 'col-max', sortable: true },
                        { key: 'profissionalNome', label: 'Profissional executante', class: 'col-max', sortable: true },
                        { key: 'status', label: 'Status', class: 'col-mix', sortable: true },
                        { key: 'numero', label: 'Protocolo de fechamento', fixed: true, class: 'col-status', sortable: true },
                        { key: 'actions', label: 'Ações', class: 'col-min text-center'}
                    ]
                },
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                ],
            }
        },
        validations() {
            return {
                unidadeId: { required: true },
                profissionalId: { required: true },
                grupoExameId: { required: true } ,
            }
        },
        unidades(){

            let unidadeOrigem = []

            if (this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(agendamento =>
                    moment(agendamento.data, 'DD/MM/YYYY').isBetween(start, end))
                        .map(unidade => {

                            this.resetFields()

                            let verifyUnidade = unidadeOrigem.find(u => u.unidadeId === unidade.unidadeId)

                            if(!verifyUnidade){

                                unidadeOrigem.push({

                                    unidadeId: unidade.unidadeId,
                                    unidadeNome: unidade.unidadeNome
                                })
                            }
                        })
            }

            return unidadeOrigem
        },
        profissionais(){

            let profissionalOrigem = []

            if (this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(u => moment(u.data, 'DD/MM/YYYY').isBetween(start, end)
                    && u.unidadeId === this.model.unidadeId)
                        .map(prof => {

                            if (profissionalOrigem){

                                let verifyProfissional = profissionalOrigem.find(u => u.profissionalId === prof.profissionalId)

                                if (!verifyProfissional) {

                                    profissionalOrigem.push({

                                        profissionalId: prof.profissionalId,
                                        profissionalNome: prof.profissionalNome,
                                    })
                                }
                            }
                        })

                profissionalOrigem.sort((a,b) => (a.profissionalNome > b.profissionalNome) ? 1 : ((b.profissionalNome > a.profissionalNome) ? -1 : 0));
            }

            return profissionalOrigem
        },
        medicos(){

            let medicoOrigem = []

            if (this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(m => moment(m.data, 'DD/MM/YYYY').isBetween(start, end)
                    && m.unidadeId === this.model.unidadeId
                    && m.profissionalId === this.model.profissionalId)
                    .map(medico => {

                        if (medicoOrigem){

                            let verifyMedico = medicoOrigem.find(u => u.medicoId === medico.medicoId)

                            if(!verifyMedico){

                                medicoOrigem.push({

                                    medicoId: medico.medicoId,
                                    medicoNome: medico.medico
                                })
                            }
                        }
                    })

                medicoOrigem.sort((a,b) => (a.medicoNome > b.medicoNome) ? 1 : ((b.medicoNome > a.medicoNome) ? -1 : 0));
            }

            return medicoOrigem
        },
        grupoExames(){

            let grupoExameOrigem = []

            if (this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.agendamentos.filter(g => moment(g.data, 'DD/MM/YYYY').isBetween(start, end)
                    && g.unidadeId === this.model.unidadeId
                    && g.profissionalId === this.model.profissionalId)
                        .map(grupo => {

                            if(grupoExameOrigem){

                                let verifyGrupoExame = grupoExameOrigem.find(e => e.grupoExameId === grupo.grupoExameId)

                                if(!verifyGrupoExame){

                                    grupoExameOrigem.push({
                                        grupoExameId: grupo.grupoExameId,
                                        grupoExame: grupo.grupoNome
                                    })
                                }
                            }
                        })

                grupoExameOrigem.sort((a,b) => (a.grupoExame > b.grupoExame) ? 1 : ((b.grupoExame > a.grupoExame) ? -1 : 0));
            }

            return grupoExameOrigem
        },
        exames(){

            let exameOrigem = []

            if (this.data.model.agendamentos) {

                this.data.model.agendamentos.filter(e => e.unidadeId === this.model.unidadeId
                    && e.profissionalId === this.model.profissionalId
                    && e.grupoExameId === this.model.grupoExameId && (!this.model.medicoId || e.medicoId === this.model.medicoId))
                        .map(exame => {

                            if (exameOrigem) {

                                let verifyExame = exameOrigem.find(u => u.exameId === exame.exameId)

                                if (!verifyExame) {

                                    exameOrigem.push({

                                        exameId: exame.exameId,
                                        exameNome: exame.exameNome,
                                    })
                                }
                            }

                        })

                exameOrigem.sort((a,b) => (a.exameNome > b.exameNome) ? 1 : ((b.exameNome > a.exameNome) ? -1 : 0));
            }

            return exameOrigem
        },
    },
    created() {

        this.repositories = {
            fechamento: this.getConectaSusRepository('exameImagem.fechamento'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        },
        this.setDefaultValues();
    },
    data() {
        return {
            selecionados: false,
            notSaveLancamentos: null,
            maxDate: null,
            model: {
                prontuario: null,
                periodo: {
                    start: null,
                    end: null,
                },
                paciente: null,
                dataNascimento: null,
                urgente: false,
                statusId: null,
                grupoExameId: null,
                medicoId: null,
                unidadeId: null,
                profissionalId: null,
                exameId: null,
            },
            enums:{
                statusFechamento,
                preferenciaNome
            },
            dataAtual: '',
            paciente: null,
            idPaciente: null,
            form: {},
            additionals: {},
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        sendLancancamento(row){

            this.idPaciente = row.item.idPaciente

            const confirm = async () => {

                this.$emit('handleAtualizaPaciente', this.idPaciente)
            }

            const cancel = async () => {

                this.$emit('handleExitLancamento')
            }

            const msg = 'Você será redirecionado para outra página. As informações não salvas serão perdidas.' +
                ' Deseja continuar?'

                this.showConfirmation(msg, confirm, cancel)

            return false
        },
        selectRealizado(){

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.showConfirmation(
                    'No status de todos os exames, será atribuído REALIZADO. Deseja continuar?',
                    async () => this.data.model.agendamentosNotSave.map((item) => {

                        item.statusId = this.enums.statusFechamento.REALIZADO
                    })
                )

            } else {

                this.showConfirmation(
                    'No status de todos os exames, será atribuído REALIZADO. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = this.enums.statusFechamento.REALIZADO
                    })
                )
            }
        },
        selectFaltou(){

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.showConfirmation(
                    'No status de todos os exames, será atribuído FALTOU. Deseja continuar?',
                    async () => this.data.model.agendamentosNotSave.map((item) => {

                        item.statusId = this.enums.statusFechamento.FALTOU
                    })
                )

            } else {

                this.showConfirmation(
                    'No status de todos os exames, será atribuído FALTOU. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = this.enums.statusFechamento.FALTOU
                    })
                )
            }
        },
        cleanSelecionados(){

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.showConfirmation(
                    'O status de todos os exames serão alterados para Selecione. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = null
                    })
                )
            } else {

                this.showConfirmation(
                    'O status de todos os exames serão alterados para Selecione. Deseja continuar?',
                    async () => this.data.model.agendamentos.map((item) => {

                        item.statusId = null
                    })
                )
            }
        },
        search(){ 

            let buscaAgendamento = this.data.model.agendamentos

            if(this.model.agendamentoNumero){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.agendamentoNumero == this.model.agendamentoNumero)
            }

            if(this.model.exameId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.exameId == this.model.exameId)
            }

            if(this.model.grupoExameId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.grupoExameId == this.model.grupoExameId)
            }

            if(this.paciente){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.pacienteId == this.paciente.id)
            }

            if (this.model.periodo.start && this.model.periodo.end){

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    moment(agendamento.data, 'DD/MM/YYYY').isBetween(start, end))
            }

            if(this.model.profissionalId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.profissionalId == this.model.profissionalId)
            }

            if(this.model.medicoId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.medicoId == this.model.medicoId)
            }

            if(this.model.prontuario){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.prontuario == this.model.prontuario)
            }

            if(this.model.unidadeId){

                buscaAgendamento = buscaAgendamento.filter(agendamento =>
                    agendamento.unidadeId == this.model.unidadeId)
            }

            this.data.model.agendamentosNotSave = buscaAgendamento
        },
        resetFields(){
            this.model.unidadeId = 
                this.centroCusto && 
                this.centroCusto.infraestrutura && 
                this.centroCusto.infraestrutura.unidade 
                    ? this.centroCusto.infraestrutura.unidade.id 
                    : null;
            this.model.profissionalId = null
            this.model.grupoExameId = null
            this.model.exameId = null
            this.data.model.agendamentosNotSave = null
        },
        resetMedico(){
            this.model.profissionalId = null
            this.model.grupoExameId = null
            this.model.exameId = null
            this.model.medicoId = null
            this.data.model.agendamentosNotSave = null
        },
        resetProfissional(){
            this.model.grupoExameId = null
            this.model.exameId = null
            this.data.model.agendamentosNotSave = null
        },
        reset(){
            this.model.medicoId = null
            this.model.grupoExameId = null
            this.model.exameId = null
            this.data.model.agendamentosNotSave = null
        },
        resetForm() {
            this.model.unidadeId = 
                this.centroCusto && 
                this.centroCusto.infraestrutura && 
                this.centroCusto.infraestrutura.unidade 
                    ? this.centroCusto.infraestrutura.unidade.id 
                    : null;
            this.model.agendamentoNumero = null
            this.model.exameId = null
            this.model.grupoExameId = null
            this.model.medicoId = null
            this.paciente = null
            this.model.profissionalId = null
            this.model.prontuario = null

            this.data.model.agendamentosNotSave = null

            if(this.data.model.agendamentosNotSave && this.data.model.agendamentosNotSave.length > 0){

                this.data.model.agendamentos.map((item) => {

                    item.statusId = null
                })

            } else {

                this.data.model.agendamentos.map((item) => {

                    item.statusId = null
                })
            }

            this.$nextTick()
                .then(() => {

                    this.setDefaultValues()
                    this.$refs.Form.resetValidator()
                })
        },
        setDefaultValues() {
            this.form = this.data.form;
            this.model = this.data.model;
            this.dataAtual = this.data.dataAtual;
            this.maxDate = moment(this.data.dataAtual).format('DD/MM/YYYY');

            this.model.periodo.start = moment(this.form.dataAtual, 'DD/MM/YYYY').toDate()
            this.model.periodo.end = moment(this.form.dataAtual, 'DD/MM/YYYY').toDate()
        },
        handleProtocoloFechamentoChange(item) {
            if (item.controlaProtocoloFechamento && item.numero !== item.protocoloFechamento) {
                this.showValidationNotification('O protocolo de fechamento informado é inválido.')
                item.protocoloFechamento = null
            }
        },
        filteredStatusOptions(regulacaoId) {
            const idsParaOcultar = [this.enums.statusFechamento.CANCELADO, this.enums.statusFechamento.REAGENDADO];
            if (regulacaoId) {
                return this.form.status.filter(status => !idsParaOcultar.includes(status.id));
            }
            return this.form.status;
        }
    },
    validate() {

        return this.$refs.Form.validate()
    },
    mixins: [repository, error, object, data, stringCase],
    mounted() {
        this.model.unidadeId = 
            this.centroCusto && 
            this.centroCusto.infraestrutura && 
            this.centroCusto.infraestrutura.unidade 
                ? this.centroCusto.infraestrutura.unidade.id 
                : null;
    },
    props: {
        data: { type: Object, required: true }
    },
}
</script>

<style scoped>
    .selectPaciente {
        cursor: pointer;
    }
</style>